import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filter, FilterOption, All, Empty, SearchAppState } from '../search-model';
import { completeUpdate, updateFilter, clearFilter } from '../search-slice';
import { ViewType } from '../../standalone-app/topic-page/models';

export interface Props {
  name: string;
  options: FilterOption[];
  label: string;
  clearText: string;
  setView: any;
  view: ViewType;
  initialValue?: string;
  hideToggle?: boolean;
}

export function BtnCheckBoxFilter(props: Props) {
  const { name, options, label, clearText, setView, view, initialValue, hideToggle } = props;

  const [checked, setChecked] = useState(() => {
    const decoded = decodeURIComponent((initialValue || '').replace(/\+/g, ' '));
    const parsed = decoded.split(',').map(v => v.trim().toLowerCase()).filter(Boolean);
    return parsed.length > 0 ? parsed : [All.toLowerCase()];
  });

  const dispatch = useDispatch();
  const items = useSelector<SearchAppState, any[]>(a => a.searchState.lists);

  const handleButtonClick = (value: string) => {
    const normalizedValue = value.toLowerCase();
    let updated = [...checked];
  
    const isAll = normalizedValue === All.toLowerCase();
  
    if (isAll && updated.includes(normalizedValue)) {
      return;
    }
  
    if (isAll) {
      updated = [All.toLowerCase()];
    } else {
      updated = updated.filter(v => v !== All.toLowerCase());
  
      if (updated.includes(normalizedValue)) {
        updated = updated.filter(v => v !== normalizedValue);
      } else {
        updated.push(normalizedValue);
      }
  
      if (updated.length === 0) {
        updated = [All.toLowerCase()];
      }
    }
  
    setChecked(updated);
  
    const newFilter: Filter = {
      filterKey: name,
      filterOptions: updated.map(option => ({
        filterKey: name,
        filterValue: option,
        filterDisplayValue: option,
      })),
      partialMatch: false,
    };
  
    dispatch(updateFilter(newFilter));
    dispatch(completeUpdate());
  
    const filtered = updated.filter(Boolean);
    const joined = filtered.join(',');
    const encodedFilter = encodeURIComponent(joined);
    
    const params = new URLSearchParams(window.location.search);
    params.delete('filter');
    let newQuery = params.toString();
    if (newQuery) {
      newQuery += `&filter=${encodedFilter}`;
    } else {
      newQuery = `filter=${encodedFilter}`;
    }
    const newUrl = `${window.location.pathname}?${newQuery}`;
    window.history.replaceState(null, '', newUrl);
    
  };
  

  const clearFilterAction = () => {
    dispatch(clearFilter());
    setChecked([All.toLowerCase()]);
  };

  const getCount = (value: string) => {
    return value.toLowerCase() === All.toLowerCase()
      ? ''
      : `(${items?.filter(item => item[name]?.toLowerCase?.() === value.toLowerCase())?.length})`;
  };

  return (
    <>
      <div className='topic-filter-top'>
        <p className='topic-filter-top-header'>
          <svg className='info-icon' aria-hidden='true' focusable='false'>
            <use xlinkHref='#filter-icon' />
          </svg>
          {label}
        </p>
        <button className='topic-filter-clear' type='button' onClick={clearFilterAction}>
          {clearText}
        </button>
      </div>

      <div className='topic-filter-content'>
        <div className='topic-filter-button-container'>
          {options?.map(option => (
            <button
              key={option.filterValue}
              type='button'
              className={`topic-filter-button ${checked.some(v => v.toLowerCase() === option.filterValue.toLowerCase()) ? 'selected' : ''}`}
              onClick={() => handleButtonClick(option.filterValue)}
            >
              {option.filterDisplayValue} {getCount(option.filterValue)}
            </button>
          ))}
        </div>

        {!hideToggle && (
          <div className='topic-filter-view-container'>
            <button
              className={`topic-filter-view-button ${view === ViewType.card ? 'topic-filter-view-button-selected' : ''}`}
              type='button'
              onClick={() => setView(ViewType.card)}
            >
              <svg className='info-icon' aria-hidden='true' focusable='false'>
                <use xlinkHref='#grid-icon' />
              </svg>
              <span className='visually-hidden'>Card</span>
            </button>
            <button
              className={`topic-filter-view-button ${view === ViewType.list ? 'topic-filter-view-button-selected' : ''}`}
              type='button'
              onClick={() => setView(ViewType.list)}
            >
              <svg className='info-icon' aria-hidden='true' focusable='false'>
                <use xlinkHref='#list-icon' />
              </svg>
              <span className='visually-hidden'>List</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}